import React, { useEffect, useState } from "react";

import cn from "classnames";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ym from "react-yandex-metrika";

import { QuestionCircleFilled, SearchOutlined, SelectOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { Popover, Steps } from "antd";

import { RootState } from "../../../store/root_reducer";
import { fetchRequest, previousFetchRequest } from "../../../store/widgets/conversions/actions";
import { ConversionsValue } from "../../../store/widgets/conversions/types";
import { IntervalType } from "../../../store/widgets/types";
import { formatNumber } from "../../../utils/table";
import { IntervalSelector } from "../components";
import { Widget } from "../widget";

import styles from "./styles.module.scss";

export interface ConversionsWidgetProps {
  className?: string;
}

export const ConversionsWidget: React.FC<ConversionsWidgetProps> = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["conversions_widget", "widget"]);

  const [interval, setInterval] = useState<IntervalType>("today");

  const selectedGate = useSelector((state: RootState) => state.User.selectedGate);
  const conversionsWidgetState = useSelector((state: RootState) => state.Widgets.Conversions);

  useEffect(() => {
    dispatch(fetchRequest({ gateId: selectedGate.id, interval }));
    dispatch(previousFetchRequest({ gateId: selectedGate.id, interval }));
  }, [dispatch, selectedGate, interval]);

  const onIntervalSelect = (value: IntervalType) => {
    setInterval(value);
    ym("reachGoal", "widget_conversions_select_interval", { interval: value });
  };

  const renderDescription = (value: ConversionsValue) => {
    return (
      <>
        <div className={styles.description}>
          {value.value ? formatNumber(value.value) : "–"}{" "}
          <span className={styles.label}>{t(`widget:intervals.${interval}`)}</span>
          {conversionsWidgetState.dateFrom && conversionsWidgetState.dateTo && (
            <Popover
              trigger="hover"
              content={t("dateRange", {
                dateFrom: conversionsWidgetState.dateFrom,
                dateTo: conversionsWidgetState.dateTo,
              })}
            >
              <QuestionCircleFilled className={styles.icon} />
            </Popover>
          )}
        </div>
        <div className={cn([styles.description, styles.compare])}>
          {value.previousValue ? formatNumber(value.previousValue) : "–"}{" "}
          <span className={styles.label}>
            {t(`widget:intervals.${interval === "today" ? "yesterday" : "weekAgo"}`)}
          </span>
          {conversionsWidgetState.dateFrom && conversionsWidgetState.dateTo && (
            <Popover
              trigger="hover"
              content={t("dateRange", {
                dateFrom: conversionsWidgetState.previousDateFrom,
                dateTo: conversionsWidgetState.previousDateTo,
              })}
            >
              <QuestionCircleFilled className={styles.icon} />
            </Popover>
          )}
        </div>
      </>
    );
  };

  const renderTitle = (field: string, diffPercentage: number) => {
    return (
      <div className={styles.title}>
        <span>{t(`steps.${field}`)}</span>
        {diffPercentage !== 0 && (
          <span
            className={cn(
              [styles.badge],
              { [styles.negative]: diffPercentage < 0 },
              { [styles.positive]: diffPercentage > 0 }
            )}
          >
            {formatNumber(diffPercentage)}%
          </span>
        )}
      </div>
    );
  };

  const renderStep = (field: string, value: ConversionsValue, Icon: any) => {
    let diffPercentage = 0;
    if (value.value && value.previousValue) {
      diffPercentage = ((value.value - value.previousValue) / ((value.value + value.previousValue) / 2)) * 100;
    }

    return (
      <Steps.Step
        status="finish"
        icon={<Icon />}
        description={renderDescription(value)}
        title={renderTitle(field, diffPercentage)}
      />
    );
  };

  return (
    <Widget
      className={props.className}
      lastUpdated={moment.utc(conversionsWidgetState.lastUpdate)}
      isLoading={conversionsWidgetState.isLoading || conversionsWidgetState.isPreviousLoading}
      title={
        <div>
          {t("title")}
          <IntervalSelector value={interval} onSelect={onIntervalSelect} />
        </div>
      }
    >
      <Steps direction="vertical" className={styles.steps}>
        {renderStep(
          "searches",
          { value: conversionsWidgetState.searches, previousValue: conversionsWidgetState.previousSearches },
          SearchOutlined
        )}
        {renderStep(
          "clicks",
          { value: conversionsWidgetState.clicks, previousValue: conversionsWidgetState.previousClicks },
          SelectOutlined
        )}
        {renderStep(
          "bookings",
          { value: conversionsWidgetState.bookings, previousValue: conversionsWidgetState.previousBookings },
          ShoppingCartOutlined
        )}
      </Steps>
    </Widget>
  );
};
